// MessageView.tsx
import SendIcon from "@mui/icons-material/Send";
import { Avatar, Box, IconButton, Skeleton, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  Conversation,
  Message,
  Paginator,
  Client as TwilioClient,
} from "@twilio/conversations";
import { useLazyGetReferralByIdQuery } from "lib/features/referral/referralService";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { messageStyles } from "styles/ChatDirectory/MessageView.styles";
import { checkoutTime } from "util/commonHelper";
import { getItemImageSource } from "util/getImageSource";
import { parseImageUrl } from "util/parseImageUrl";

type Props = {
  conversationSid: string;
  getClient: () => Promise<TwilioClient>; // must resolve to a connected client
  currentUserId: string;
  avatar: string;
  name: string;
};

type ReferralMessageProps = {
  body: string;
  isMine: boolean;
  isMobile: boolean;
};

function ReferralMessage({ body, isMine, isMobile }: ReferralMessageProps) {
  const [getReferralById, { data: referralData, isLoading: isReferralLoading }] =
    useLazyGetReferralByIdQuery();
  const navigate = useNavigate();


  const containsReferralLink = (text: string): boolean => {
    const referralPattern = /https:\/\/app\.refereree\.com\/checkout\/\d+/;
    return referralPattern.test(text);
  };

  const extractIdFromMessage = (text: string): string | null => {
    const match = text.match(/https:\/\/app\.refereree\.com\/checkout\/(\d+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    if (!body || !containsReferralLink(body)) return;
    const referralId = extractIdFromMessage(body);
    if (!referralId) return;
    getReferralById(Number(referralId));
  }, [body, getReferralById]);

  return (
    <Box sx={{ px: isMobile ? 0.25 : 0, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: isMine ? "flex-end" : "flex-start",
          cursor: "pointer",
        }}
      >
        {isReferralLoading || !referralData ? (
          <Skeleton
            variant="rectangular"
            width={300}
            height={150}
            sx={messageStyles.messageSkeleton(isMine)}
          />
        ) : (
          <Box
            onClick={() => navigate(`/checkout/${referralData?.id}`)}
            sx={messageStyles.imageMessage(isMobile, isMine)}
          >
            <Box sx={{ alignItems: "center", justifyContent: "center" }}>
              <img
                src={getItemImageSource(referralData?.item)}
                style={messageStyles.image}
              />
            </Box>
            <Typography sx={messageStyles.itemDetailsText}>
              {referralData?.item?.item_type === 4
                ? referralData?.item.farehabor_item_details?.name
                : referralData?.item?.itemDetails[0]?.item_name}
              <br />
              {moment(referralData?.selected_farehabor_availability?.start_at).format("MM/DD/YY")},&nbsp;  
              {referralData?.item?.item_type === 4
                ? checkoutTime(referralData?.selected_farehabor_availability?.start_at)
                : checkoutTime(referralData?.availability?.start_time)}
              {" - "}
              {referralData?.item?.item_type === 4
                ? checkoutTime(referralData?.selected_farehabor_availability?.end_at)
                : checkoutTime(referralData?.availability?.end_time)}
            </Typography>
            <Typography sx={messageStyles.appText}>
              app.refereree.com
            </Typography>
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          color: "text.disabled",
          mt: 0.25,
          textAlign: isMine ? "right" : "left",
        }}
      >
        {referralData
          ? `${moment(referralData?.created_time).format("MM/DD/YYYY")}, ${checkoutTime(
            referralData.created_time,
          )}`
          : ""}
      </Typography>
    </Box>
  );
}

export default function ConversationView({ conversationSid, getClient, currentUserId, avatar, name }: Props) {
  const pathname = window.location.pathname; // e.g. "/chat/123/456"

  const parts = pathname.split("/").filter(Boolean);
  const lastTwo = parts.slice(-3)
  console.log("Last two parts of the path:", lastTwo);
  avatar = decodeURIComponent(lastTwo[0] || "");
  name = decodeURIComponent(lastTwo[1] || "");
  let myavatar = decodeURIComponent(lastTwo[2] || "");
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 899px)");

  const [convo, setConvo] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [olderPage, setOlderPage] = useState<Paginator<Message> | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep a stable reference so effect doesn't rerun unnecessarily
  const getClientRef = useRef(getClient);
  useEffect(() => { getClientRef.current = getClient; }, [getClient]);

  useEffect(() => {
    let cancelled = false;
    let conversation: Conversation | null = null;

    // Handlers (stable references for cleanup)
    const onAdded = (m: Message) => setMessages(prev => [...prev, m]);
    const onUpdated = ({ message }: { message: Message }) =>
      setMessages(prev => prev.map(x => (x.sid === message.sid ? message : x)));
    const onRemoved = (m: Message) =>
      setMessages(prev => prev.filter(x => x.sid !== m.sid));

    (async () => {
      const client = await getClientRef.current();
      conversation = await client.getConversationBySid(conversationSid);
      if (cancelled) return;

      setConvo(conversation);

      const page = await conversation.getMessages(50);
      if (cancelled) return;

      setMessages(page.items);
      setOlderPage(page.hasPrevPage ? page : null);

      // Remove any stale listeners first, then attach fresh ones
      conversation.removeAllListeners("messageAdded");
      conversation.removeAllListeners("messageUpdated");
      conversation.removeAllListeners("messageRemoved");

      conversation.on("messageAdded", onAdded);
      conversation.on("messageUpdated", onUpdated);
      conversation.on("messageRemoved", onRemoved);

      // Scroll after paint
      setTimeout(scrollToBottom, 0);
    })();

    return () => {
      cancelled = true;
      if (conversation) {
        conversation.off("messageAdded", onAdded);
        conversation.off("messageUpdated", onUpdated);
        conversation.off("messageRemoved", onRemoved);
      }
    };
  }, [conversationSid]);

  const onTopReached = async () => {
    if (!olderPage) return;
    const prev = await olderPage.prevPage();
    setMessages(prevMsgs => [...prev.items, ...prevMsgs]);
    setOlderPage(prev.hasPrevPage ? prev : null);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !convo) return;
    try {
      setSending(true);
      await convo.sendMessage(text); // rely on messageAdded event to render
      setInput("");
      setTimeout(scrollToBottom, 0);
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // “Grouped” display can be added later; simple list for now
  const items = useMemo(() => messages, [messages]);
  console.log("Rendering messages:", items);
  return (
    <Box
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateRows: "56px 1fr 64px",
        // On mobile, make sure input isn't hidden by the keyboard
        // Safari handles this with 100dvh on the parent container
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 56,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name || `Conversation`}
        </Typography>
      </Box>

      {/* Messages */}
      <Box
        ref={scrollRef}
        onScroll={(e) => {
          const target = e.currentTarget;
          if (target.scrollTop <= 0) onTopReached();
        }}
        sx={{
          overflowY: "auto",
          p: isMobile ? 1.25 : 2,
          background: theme.palette.mode === "light" ? "#fafafa" : "transparent",
        }}
      >
        {items.map((m) => {
          const isMine = String(m.author) === String(currentUserId);
          const body = (m as any).body as string | undefined;
          if (body && body.includes("https://app.refereree.com/checkout/")) {
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMine ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Avatar
                  src={isMine ? parseImageUrl(myavatar) : parseImageUrl(avatar)}
                  alt={""}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 12,
                  }}
                >
                  {(isMine ? "You" : name)?.substring(0, 1)?.toUpperCase()}
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {/* NAME ABOVE BUBBLE */}
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "text.disabled",
                      mb: 0.25,
                      textAlign: isMine ? "right" : "left",
                      ml: isMine ? 0 : 0.5,
                      mr: isMine ? 0.5 : 0,
                    }}
                  >
                    {isMine ? "You" : name}
                  </Typography>
                  <ReferralMessage
                    key={m.sid}
                    body={body}
                    isMine={isMine}
                    isMobile={isMobile}
                  />
                </Box>
              </Box>
            );
          }

          return (
            <Box
              key={m.sid}
              sx={{
                display: "flex",
                justifyContent: isMine ? "flex-end" : "flex-start",
                mb: 1,
                px: isMobile ? 0.25 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMine ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                {/* AVATAR */}
                <Avatar
                  src={isMine ? parseImageUrl(myavatar) : parseImageUrl(avatar)}
                  alt={""}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 12,
                  }}
                >
                  {(isMine ? "You" : name)?.substring(0, 1)?.toUpperCase()}
                </Avatar>

                {/* MESSAGE + NAME */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {/* NAME ABOVE BUBBLE */}
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "text.disabled",
                      mb: 0.25,
                      textAlign: isMine ? "right" : "left",
                      ml: isMine ? 0 : 0.5,
                      mr: isMine ? 0.5 : 0,
                    }}
                  >
                    {isMine ? "You" : name}
                  </Typography>

                  {/* MESSAGE BUBBLE */}
                  <Box
                    sx={{
                      maxWidth: isMobile ? "82%" : "68%",
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: isMine ? "#DAF3D9" : "white",
                      color: "inherit",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                    }}
                  >
                    {"body" in m && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {(m as any).body}
                      </Typography>
                    )}

                    <Typography
                      sx={{
                        fontSize: 8,
                        color: "text.disabled",
                        mt: 0.5,
                        textAlign: "right",
                      }}
                    >
                      {m.dateCreated?.toLocaleString?.() ?? ""}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Composer */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "95% 5%",
          position: "fixed",
          bottom: 0,
          width: "calc(100% - 400px)",
          bgcolor: "background.paper",
          gap: 1,
          p: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          // keep input above iOS keyboard
          background: theme.palette.background.paper,
        }}
      >
        <TextField
          size="small"
          placeholder="Type a message"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          inputProps={{ "aria-label": "Message input" }}
        />
        <IconButton onClick={handleSend} disabled={sending || !input.trim()} aria-label="Send">
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}